/**
 * Client of the compute Web Worker (workers/compute.js → compute/impl.js): parsing the official
 * data, the planning engine of the static-hosting backend, road analysis and right-of-way labels
 * run off the main thread, so dragging and zooming the map stay smooth on phones.
 * Falls back to running compute/impl.js in the page (same results, lazily loaded) when the worker
 * cannot start (no module workers, blocked, crashed at load) or a result cannot be transferred.
 *   compute(fn, args, { signal, timeout }) → Promise<result>
 */
const WORKER_URL = '/assets/b/compute-KZSDWV7L.js'; // rewritten to the hashed bundle by scripts/build-cf.mjs
const MODE = typeof document !== 'undefined' && document.querySelector('meta[name="qh-backend"]')?.content === 'edge' ? 'edge' : 'server';
const START_TIMEOUT = 15000;

let worker = null;
let started = null;
let local = null;
let seq = 0;
const pending = new Map();
const stats = { mode: 'starting', calls: 0, local: 0 };

const abortError = (signal) => signal?.reason || new DOMException('Aborted', 'AbortError');
function toError(e) {
  if (e?.name === 'AbortError') return new DOMException(e.message || 'Aborted', 'AbortError');
  return Object.assign(new Error(e?.message || 'Lỗi tính toán'), { name: e?.name === 'DataCloneError' ? 'DataCloneError' : 'Error', status: e?.status, issues: e?.issues, detail: e?.detail });
}

function inPage() {
  return (local ||= import('./compute/impl.js').then((m) => { m.configure({ mode: MODE }); return m; }));
}

function failAll(why) {
  for (const [, p] of pending) p.reject(Object.assign(new Error(why), { retryLocal: true }));
  pending.clear();
}

/** Start the worker once; resolves true when it answered, false → in-page fallback. */
function start() {
  if (started) return started;
  started = new Promise((resolve) => {
    let w;
    try {
      if (typeof Worker === 'undefined') throw new Error('no Worker');
      w = new Worker(WORKER_URL, { type: 'module', name: 'qh-compute' });
    } catch {
      stats.mode = 'page';
      resolve(false);
      return;
    }
    let ready = false;
    const give = (why) => {
      clearTimeout(timer);
      try { w.terminate(); } catch { /* already gone */ }
      worker = null;
      stats.mode = 'page';
      stats.reason = why;
      failAll(why);
      resolve(false);
    };
    const timer = setTimeout(() => give('timeout'), START_TIMEOUT);
    w.onmessage = (e) => {
      const m = e.data || {};
      if (m.type === 'ready') {
        ready = true;
        clearTimeout(timer);
        worker = w;
        stats.mode = 'worker';
        resolve(true);
        return;
      }
      const p = pending.get(m.id);
      if (!p) return;
      pending.delete(m.id);
      if (m.ok) p.resolve(m.value); else p.reject(toError(m.error));
    };
    w.onerror = (e) => {
      e.preventDefault?.();
      if (!ready) give('load error');
      else console.warn('compute worker error', e.message || e);
    };
    w.onmessageerror = () => console.warn('compute worker: message could not be read');
    w.postMessage({ type: 'init', mode: MODE });
  });
  return started;
}

async function runLocal(fn, args, signal) {
  stats.local++;
  const m = await inPage();
  if (typeof m.handlers[fn] !== 'function') throw new Error(`Không có phép tính ${fn}`);
  return m.handlers[fn](...args, signal || new AbortController().signal);
}

/** Run handler `fn` of compute/impl.js with plain-data `args`. */
export async function compute(fn, args = [], { signal, timeout = 90000 } = {}) {
  if (signal?.aborted) throw abortError(signal);
  stats.calls++;
  if (!(await start())) return runLocal(fn, args, signal);
  const id = ++seq;
  try {
    return await new Promise((resolve, reject) => {
      let timer = 0;
      const finish = () => { clearTimeout(timer); signal?.removeEventListener('abort', onAbort); };
      const onAbort = () => {
        if (!pending.delete(id)) return;
        finish();
        worker?.postMessage({ type: 'abort', id });
        reject(abortError(signal));
      };
      pending.set(id, { resolve: (v) => { finish(); resolve(v); }, reject: (e) => { finish(); reject(e); } });
      timer = setTimeout(() => {
        if (!pending.delete(id)) return;
        finish();
        worker?.postMessage({ type: 'abort', id });
        reject(Object.assign(new Error('Hết thời gian tính toán.'), { status: 0 }));
      }, timeout);
      signal?.addEventListener('abort', onAbort, { once: true });
      try {
        worker.postMessage({ type: 'call', id, fn, args });
      } catch (e) {
        pending.delete(id);
        finish();
        reject(Object.assign(toError(e), { retryLocal: true }));
      }
    });
  } catch (e) {
    // the worker could not take the arguments or return the result: same work in the page
    if (e?.retryLocal || e?.name === 'DataCloneError') return runLocal(fn, args, signal);
    throw e;
  }
}

/** Start the worker early (and the engine's legends download in edge mode). */
export const warmCompute = () => { start(); };

/** Where the work runs: { mode: 'worker' | 'page' | 'starting', calls, local, reason? } (debug). */
export const computeInfo = () => ({ ...stats });
